// src/components/discovery/DiscoverySectionPage.jsx
//
// One reusable page shell for every "See all" destination (Fresh
// Moments, Rising, and eventually Near You / Closing Now if those
// get their own full pages too). Built this way specifically so the
// nav can never be forgotten on a new page — there's only one place
// this shell is defined — and so every destination automatically gets
// the same anti-dead-end treatment: filters, a live count, a
// cross-promo footer into other sections, and the same dark CTA
// pattern already proven on Discover.jsx.
//
// This component assumes your app renders <Nav /> once in a shared
// layout (e.g. App.jsx wrapping <Outlet />), NOT inside each page.
// If Nav is currently pasted into Discover.jsx directly rather than
// coming from a layout wrapper, pull it out into a layout route first
// — otherwise every new page like this will need it re-added by hand,
// which is exactly the thing you flagged wanting to avoid.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NICHE_BROWSE_LIST, normalizeNicheKey } from "../niche";
import DropCard from "./DropCard";
import CreatorCard from "./CreatorCard";
import "../../screens/Discover.css";
import "./DiscoverySectionPage.css";

const NICHE_FILTERS = [{ key: "all", label: "All" }, ...NICHE_BROWSE_LIST];
const PAGE_SIZE = 12;

/**
 * @param {object} props
 * @param {string} props.title - e.g. "Fresh Moments"
 * @param {string} props.eyebrow - short philosophy line under the title,
 *   e.g. "Newest first. No ranking, just the clock."
 * @param {"drop"|"creator"} props.itemType
 * @param {(opts: {niche: string, search: string}) => any[]} props.getItems
 *   - a function (usually a thin wrapper around one of the
 *   computeXxx() functions in utils/discoveryPools.js) that returns
 *   the FULL pool for the given filters, unlimited. This component
 *   handles slicing it into pages itself.
 * @param {(item:any) => object} [props.getCardProps] - per-item extra
 *   props to pass into DropCard/CreatorCard (access, openDropCount,
 *   followingIds, isNew, etc.) — whatever that card type needs beyond
 *   the item itself.
 * @param {string} [props.emptyMessage]
 * @param {Array<{key:string, title:string, seeAllPath:string, itemType:"drop"|"creator", items:any[], getCardProps?:(item:any)=>object}>} [props.crossPromo]
 *   - small teaser sections rendered at the bottom, pulling from OTHER
 *   discovery pools so this page never dead-ends. Pass 4-6 items per
 *   section; this component doesn't fetch them, the wrapper page does
 *   (it already has the data via useDiscoveryData()).
 * @param {{seenCount:number, moreCount:number}} [props.ctaStats] - feeds
 *   the closing dark CTA, same pattern as Discover.jsx's gal-end-card.
 */
export default function DiscoverySectionPage({
  title,
  eyebrow,
  itemType,
  getItems,
  getCardProps = () => ({}),
  emptyMessage = "Nothing here right now — check back soon.",
  crossPromo = [],
  ctaStats,
}) {
  const [niche, setNiche] = useState("all");
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const allItems = useMemo(
    () => getItems({ niche, search }),
    [getItems, niche, search]
  );

  const visibleItems = allItems.slice(0, visibleCount);
  const hasMore = visibleCount < allItems.length;

  const handleNicheChange = (key) => {
    setNiche(key === "all" ? "all" : normalizeNicheKey(key));
    setVisibleCount(PAGE_SIZE); // reset pagination when filters change
  };

  const Card = itemType === "drop" ? DropCard : CreatorCard;
  const gridClass = itemType === "drop" ? "gal-closing-grid" : "gal-disc-grid";

  return (
    <div className="gal-page">
      {/* breadcrumb — always back to Discover, never just the browser
          back button, so the page reads as part of one connected space */}
      <div className="dsp-crumb-row">
        <Link to="/gallery" className="dsp-crumb">← Discover</Link>
      </div>

      <div className="dsp-header">
        <h1 className="dsp-title">{title}</h1>
        {eyebrow && <p className="dsp-eyebrow">{eyebrow}</p>}
        <div className="dsp-count">{allItems.length} {itemType === "drop" ? "open" : ""} right now</div>
      </div>

      <div className="gal-search-row dsp-filter-row">
        <div className="gal-search-pill">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            placeholder={itemType === "drop" ? "Search drops, creators, niches..." : "Search creators, niches, topics..."}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setVisibleCount(PAGE_SIZE); }}
          />
        </div>
        <div className="gal-niche-filters">
          {NICHE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`gal-nf${niche === f.key ? " on" : ""}`}
              onClick={() => handleNicheChange(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="gal-grid-page dsp-grid-page">
        {visibleItems.length === 0 ? (
          <div className="gal-empty"><p>{emptyMessage}</p></div>
        ) : (
          <div className={gridClass}>
            {visibleItems.map((item) => (
              <Card key={item.id} {...getCardProps(item)} />
            ))}
          </div>
        )}

        {hasMore && (
          <button
            type="button"
            className="dsp-load-more"
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
          >
            Show {Math.min(PAGE_SIZE, allItems.length - visibleCount)} more
          </button>
        )}

        {/* cross-promo — never let a full page be a dead end */}
        {crossPromo.map((section) => {
          if (!section.items?.length) return null;
          const SectionCard = section.itemType === "drop" ? DropCard : CreatorCard;
          return (
            <div className="dsp-crosspromo" key={section.key}>
              <div className="gal-grid-sec">
                <span className="gal-gs-title">{section.title}</span>
                <Link to={section.seeAllPath} className="dsp-crosspromo-link">See all →</Link>
                <div className="gal-gs-line" />
              </div>
              <div className="gal-rail">
                {section.items.map((item) => (
                  <div className="gal-rail-item" key={item.id}>
                    <SectionCard
                      {...(section.getCardProps ? section.getCardProps(item) : { [section.itemType]: item })}
                      compact
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {ctaStats && (
          <div className="gal-end-card">
            <div className="gal-end-title">
              You&apos;ve seen <em>{ctaStats.seenCount}</em> here today.
            </div>
            <span className="gal-end-hand">{ctaStats.moreCount} more are posting right now</span>
            <Link to="/gallery" className="gal-end-btn">Back to Discover →</Link>
          </div>
        )}
      </div>
    </div>
  );
}